import { useEffect, useRef, useState } from 'react'

const PULL_THRESHOLD_PX = 72
const MAX_PULL_VISUAL_PX = 96

function getScrollTop() {
  return (
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  )
}

/**
 * Mobile pull-down-to-refresh: reloads the app when the user pulls past the threshold at scroll top.
 */
export function usePullToRefresh(enabled) {
  const [pullDistance, setPullDistance] = useState(0)
  const startY = useRef(0)
  const startX = useRef(0)
  const active = useRef(false)
  const pullRef = useRef(0)
  const lockedAxis = useRef(null) // 'x' | 'y' | null

  useEffect(() => {
    if (!enabled) return undefined

    const onTouchStart = e => {
      if (getScrollTop() > 8) return
      startY.current = e.touches[0].clientY
      startX.current = e.touches[0].clientX
      active.current = true
      pullRef.current = 0
      lockedAxis.current = null
    }

    const onTouchMove = e => {
      if (!active.current || getScrollTop() > 8) return
      const dy = e.touches[0].clientY - startY.current
      const dx = e.touches[0].clientX - startX.current

      // Lock gesture direction once user moves a bit. If horizontal, bail out so
      // horizontal tab swipes don't trigger pull-to-refresh.
      if (!lockedAxis.current) {
        const slop = 8
        if (Math.abs(dx) < slop && Math.abs(dy) < slop) return
        lockedAxis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      }

      if (lockedAxis.current !== 'y') return
      if (dy > 0) {
        e.preventDefault()
        pullRef.current = Math.min(dy, MAX_PULL_VISUAL_PX)
        setPullDistance(pullRef.current)
      }
    }

    const resetGesture = () => {
      active.current = false
      pullRef.current = 0
      setPullDistance(0)
      lockedAxis.current = null
    }

    const onTouchEnd = () => {
      if (!active.current) return
      const dist = pullRef.current
      resetGesture()
      if (dist >= PULL_THRESHOLD_PX && getScrollTop() <= 8) {
        window.location.reload()
      }
    }

    const onTouchCancel = () => {
      resetGesture()
    }

    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('touchend', onTouchEnd, { passive: true })
    window.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [enabled])

  return pullDistance
}
